/**
 * Data Intelligence Review — the desk's inbox.
 *
 * Every record with a question open, worst first: the ones whose answers the
 * pipeline grid shows, then the ones the sources disagree on. A row opens the
 * review on that record; the labels no field holds yet sit below the records,
 * each added one at a time.
 *
 * Found datapoints can be filtered by label or file, grouped under the file
 * they were read from, and dismissed several at a time — the selected ones,
 * every one from one file, the general-ledger account lines, or the ones no
 * live deal states any more. A bulk dismissal is a preview first: the server
 * counts what it would dismiss and names the labels, and only a second click
 * on those numbers dismisses exactly those rows, each audited as if dismissed
 * by hand. Nothing in bulk ever adds a field.
 *
 * Three desk-wide actions live in the header because they act on the whole
 * book rather than on the record open beside them, and their reports land
 * below. Two of them are minutes of work and the third writes nothing, so each
 * says what it does:
 *
 *   * **Re-parse the desk** re-reads every stored package with the current
 *     engine. It writes nothing — every difference becomes a proposal a person
 *     answers field by field.
 *   * **Reconcile deals** runs every fill the platform has on every promoted
 *     deal until a pass writes nothing, accepting a re-parse only where BOTH
 *     readings were the engine's. A value a person decided is never touched; it
 *     is listed as needing that person. Preview first, always; apply is a second
 *     click on the numbers the preview showed.
 *   * **Why fields are empty** says, for every empty field on every deal,
 *     whether a source states it. Reads only.
 *
 * There is no accept-all, here or anywhere: 74 of the 116 proposals on the desk
 * the day the re-parse tab was written were values a person had established, so
 * one button would have overwritten 74 answers somebody gave. A count of the
 * rows that touch a person's answer leads instead.
 */
import { useEffect, useRef, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, CircleSlash, Flag, ListChecks, Loader2, RefreshCw, Search, ShieldQuestion, UserCheck } from 'lucide-react'

import { useLocalAuth } from '@/auth/LocalAuthContext'
import { canEditModule } from '@/auth/modulePermission'
import DialogShell from '@/components/crm/DialogShell'
import GapCensusReport from '@/components/intake/GapCensusReport'
import ReconcileReport from '@/components/intake/ReconcileReport'
import { runGapCensus, type CensusProgress } from '@/components/intake/gapCensusRun'
import { runReconciliation, type ReconcileProgress } from '@/components/intake/reconcileRun'
import {
  INBOX_PAGE,
  dataReview,
  dataReviewKeys,
  invalidateAfterDecision,
  refusalOf,
  type DataReviewDeal,
  type DataReviewItem,
  type DiscoveryDismissInput,
  type DiscoveryDismissResult,
} from '@/services/dataReview'
import type { DiscoveredDatapoint } from '@/services/discoveredDatapoints'
import {
  ingestionService,
  type GapCensusDesk,
  type ReconcileResponse,
} from '@/services/ingestion'
import { inboxQuery, useDataReviewInboxView } from '@/store/dataReviewInboxStore'
import DataReviewPanel from './DataReviewPanel'
import { DiscoveredRow } from './DiscoveredRow'
import { BAND } from './discoveryFormat'
import { groupFoundByFile, matchesFoundFilter } from './discoveryEvidence'
import { SOURCE_LABEL, describeSettled, isOwed, kindMeta, plural } from './dataReviewFormat'

function statusTone(status: string): string {
  if (status === 'converted') return 'text-emerald-300/90 bg-emerald-400/10'
  if (status === 'declined') return 'text-stone-400 bg-stone-400/10'
  return 'text-amber-300 bg-amber-400/10'
}

/** The questions a record opens with, most urgent first, as a short line. Only
 *  the decisions it owes are named and counted, so the line agrees with the Open
 *  column beside it; the rows that are not owed (a gap, a re-read the sweep
 *  applies) are said as one count after them. A server that files no bucket is
 *  read as before: every row a question. */
function firstQuestions(deal: DataReviewDeal, limit = 3): string {
  const owed = deal.items.filter(isOwed)
  const shown = owed.slice(0, limit).map((item) => {
    const meta = kindMeta(item.kind)
    return `${item.label} (${meta.label})`
  })
  const more = owed.length - shown.length
  const line = more > 0 ? `${shown.join(', ')} +${more} more` : shown.join(', ')
  const notOwed = deal.items.length - owed.length
  if (notOwed === 0) return line
  const also = `${notOwed} not owed`
  return line ? `${line} · ${also}` : also
}

/** Said once where an account can read the review but not change anything in it. */
const NEEDS_EDIT =
  'Your role can read the Data Intelligence Review but not change anything in it — Intake edit access is needed.'

/** A found datapoint row that carries its datapoint — every discovery row does. */
type FoundItem = DataReviewItem & { detail: { discovered: DiscoveredDatapoint } }

const hasDiscovered = (item: DataReviewItem): item is FoundItem => Boolean(item.detail?.discovered)

/**
 * A bulk dismissal on its way: what it covers, what the server's preview
 * counted, and the note each dismissal will carry. Nothing is written until
 * the person confirms the numbers the preview showed.
 */
type BulkDismissal = {
  /** What it covers, in a sentence, for the confirmation. */
  what: string
  input: Omit<DiscoveryDismissInput, 'preview' | 'note'>
  preview: DiscoveryDismissResult | null
  note: string
  applying: boolean
  error: string | null
}

/** How many of the preview's labels the confirmation lists. */
const LISTED_LABELS = 10

/**
 * What a bulk dismissal's preview counts that the list above does not show,
 * in a sentence — or null when the list shows them all. "Dismiss account-code
 * lines" reaches every account line, the ones no live deal backs included,
 * while "Show unbacked" leaves those off the list; and a list cut to the
 * strongest leaves the rest unseen. The count includes them either way, so the
 * confirmation says so before anyone confirms it.
 */
function offListNote(
  preview: DiscoveryDismissResult,
  listed: ReadonlySet<string>,
  { hidingUnbacked, cutAt }: { hidingUnbacked: boolean; cutAt: number | null },
): string | null {
  const off = preview.count - preview.ids.filter((id) => listed.has(id)).length
  if (off <= 0) return null
  const one = off === 1
  const them = one ? 'it' : 'them'
  const lead = one ? 'One of them is not on the list above' : `${off} of them are not on the list above`
  const why = hidingUnbacked && cutAt !== null
    ? ` — found datapoints no live deal states any more, which the list leaves off while “Show unbacked” is off, `
      + `or ones past the ${cutAt} with the strongest evidence it shows`
    : hidingUnbacked
      ? `: no live deal states ${them} any more, and the list leaves such rows off while “Show unbacked” is off`
      : cutAt !== null
        ? ` — past the ${cutAt} with the strongest evidence it shows`
        : ''
  return `${lead}${why}. The count includes ${them}.`
}

const TOGGLE = 'rounded border px-2 py-1 text-[10px] uppercase tracking-wide transition-colors'
const toggleTone = (on: boolean) =>
  on ? 'border-amber-400/30 bg-amber-400/15 text-amber-200' : 'border-stone-700 text-stone-400 hover:text-stone-200'
const BULK_BUTTON =
  'rounded border border-stone-700 px-2 py-1 text-[10px] uppercase tracking-wide text-stone-300 transition-colors hover:bg-stone-400/10 disabled:opacity-50'

export default function DataReviewInbox() {
  const queryClient = useQueryClient()
  const { localUser } = useLocalAuth()
  // What this account may change. Presentation only: the API refuses the rest
  // either way, and an absent profile draws everything rather than blank it.
  const mayDecide = canEditModule(localUser, 'intake')
  const mayAddFound = mayDecide && canEditModule(localUser, 'pipeline')
  const [panelFor, setPanelFor] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Why the server refused a found datapoint's Add or dismissal, kept with the
  // row it was refused for: said on that row while it is listed.
  const [refused, setRefused] = useState<{ id: string; message: string } | null>(null)
  // The records come a page at a time, worst first. "Show more" asks for a longer
  // first page rather than stitching pages together, so the list is always one
  // answer the server ranked. Whether the found datapoints no live deal backs
  // are listed is part of the same view (left off unless asked for). The view
  // is shared with the Intake tab's badge, which reads the same answer rather
  // than a second read of the desk; it starts afresh each time the inbox opens.
  const { limit, includeUnbacked: showUnbacked, setLimit, setIncludeUnbacked, reset } = useDataReviewInboxView()
  useEffect(() => reset, [reset])
  // The found datapoints: a filter, a view by file, the rows picked for a bulk
  // dismissal and the dismissal itself while it waits for its confirmation.
  const [foundFilter, setFoundFilter] = useState('')
  const [byFile, setByFile] = useState(false)
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set())
  const [bulk, setBulk] = useState<BulkDismissal | null>(null)

  const { data, isLoading, isError, error: loadError, isFetching } = useQuery({
    // The Intake tab badges from this same read and used to fetch the desk again
    // every time it was opened. A decision refreshes it at once regardless.
    ...inboxQuery({ limit, includeUnbacked: showUnbacked }),
    placeholderData: keepPreviousData,
  })

  // The standing sweep (`app.core.desk_reconciliation_scheduler`) runs this
  // same reconciliation on its own interval, so a fix to the engine reaches
  // deals already on the desk without anyone clicking "Reconcile deals."
  // Surfaced here rather than left to a terminal or a log line, because a
  // sweep nobody can see is a sweep nobody trusts.
  const { data: latestSweep } = useQuery({
    queryKey: ['desk-reconciliation-runs', 'latest'],
    queryFn: () => ingestionService.reconcileRunsLatest(),
    refetchInterval: 5 * 60 * 1000,
  })
  // A deal the automated sweep would not reconcile because its stored package
  // could not be read (nd-001): the line says so, not only the report behind it.
  const sweepHeld = (latestSweep?.run?.report?.needsReupload ?? []).filter((e) => e.held).length
  // Deals the census could not read at all (nd4-intake-4): their gaps are
  // unmeasured, so the miss count is not comparable with the last sweep's.
  const sweepCensus = latestSweep?.run?.census as
    | { dealsCensused?: number; dealsUnsearched?: number }
    | null
    | undefined
  const sweepUnsearched = Number(sweepCensus?.dealsUnsearched ?? 0)
  const sweepCensused = Number(sweepCensus?.dealsCensused ?? 0)

  const sweep = useMutation({
    // `refresh` is required: a record already carrying proposals is skipped by
    // design, so without it a re-run after an engine change reports nothing and
    // leaves the stale proposals in place.
    mutationFn: () => ingestionService.proposeReparseAll(50, true),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: dataReviewKeys.all }),
  })

  const [report, setReport] = useState<ReconcileResponse | null>(null)
  const [progress, setProgress] = useState<ReconcileProgress | null>(null)
  const reconcile = useMutation({
    // Many bounded requests, not one long one. The server answers within a time
    // budget and names what it did not start; this drives it to completion.
    // A single request covering the desk is one the ingress kills, and the
    // browser then blames CORS on a route whose CORS is correct.
    mutationFn: (apply: boolean) => {
      setProgress(null)
      return runReconciliation({
        apply,
        call: ({ apply: a, ids }) => ingestionService.reconcileDesk({ apply: a, ids }),
        onProgress: setProgress,
      })
    },
    onSuccess: (result) => {
      setReport(result)
      setProgress(null)
      // A preview still records re-parse proposals; an apply changes deals.
      queryClient.invalidateQueries({ queryKey: dataReviewKeys.all })
      if (result.applied) {
        void invalidateAfterDecision(queryClient)
      }
    },
  })

  const [census, setCensus] = useState<GapCensusDesk | null>(null)
  const [censusProgress, setCensusProgress] = useState<CensusProgress | null>(null)
  const gapCensus = useMutation({
    // Read-only, and batched for the same reason the reconciliation is: opening
    // every stored source of every deal is minutes of work, and a request the
    // ingress kills reaches the browser as a phantom CORS failure.
    mutationFn: () => {
      setCensusProgress(null)
      return runGapCensus({
        call: ({ ids, search }) => ingestionService.gapCensusDesk({ ids, search }),
        onProgress: setCensusProgress,
      })
    },
    onSuccess: (result) => {
      setCensus(result)
      setCensusProgress(null)
      setReport(null)
    },
  })

  // A report lands below the records; the reviewer asked for it, so bring it
  // into view rather than leaving a one-line summary above a list they scroll past.
  const reportRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (report || census) reportRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }, [report, census])

  const deals = data?.deals ?? []
  const totals = data?.totals
  const page = data?.page
  const discovery = data?.discovery
  const found = discovery?.available ? discovery.items.filter(hasDiscovered) : []
  // Null from a server that does not tell backed rows from unbacked ones.
  const unbacked = totals?.discoveryUnbacked ?? null
  // The unbacked ones the list is leaving off right now.
  const hiddenUnbacked = !showUnbacked && unbacked !== null ? unbacked : 0
  const shown = found.filter((item) => matchesFoundFilter(item.detail.discovered, foundFilter))
  const shownIds = shown.map((item) => item.detail.discovered.id)
  // Only rows still on the list count: a row answered since it was picked has gone.
  const pickedIds = found.map((item) => item.detail.discovered.id).filter((id) => picked.has(id))
  const allShownPicked = shownIds.length > 0 && shownIds.every((id) => picked.has(id))
  // A refusal whose row is no longer listed — decided elsewhere since — is said
  // where the desk's errors are, so it is not lost with the row.
  const refusedOffList = refused && !found.some((item) => item.id === refused.id) ? refused.message : null
  const failure = error ?? refusedOffList
  // What a bulk dismissal's preview counted beyond the rows on the list.
  const previewOffList = bulk?.preview
    ? offListNote(bulk.preview, new Set(found.map((item) => item.detail.discovered.id)), {
      hidingUnbacked: hiddenUnbacked > 0,
      cutAt: discovery?.truncated ? found.length : null,
    })
    : null

  // These act on the whole desk, so they read as one row of equal actions
  // rather than three chips wedged beside a title. Each says what it does to
  // the book, because two of them are minutes of work and the third writes
  // nothing and should not look like the two that can.
  const deskActions = (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => sweep.mutate()}
        disabled={!mayDecide || sweep.isPending || reconcile.isPending}
        title={mayDecide
          ? 'Re-read every stored package with the current engine. Writes nothing — every difference becomes a proposal you answer.'
          : NEEDS_EDIT}
        className="flex items-center gap-2 rounded border border-amber-400/30 bg-amber-400/[0.06] px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-amber-200 transition-colors hover:bg-amber-400/15 disabled:opacity-50"
      >
        {sweep.isPending
          ? <Loader2 size={13} className="animate-spin" />
          : <RefreshCw size={13} />}
        Re-parse the desk
      </button>
      <button
        type="button"
        onClick={() => reconcile.mutate(false)}
        disabled={!mayDecide || sweep.isPending || reconcile.isPending}
        title={mayDecide
          ? 'Preview every fill on every promoted deal — nothing is written until you apply'
          : NEEDS_EDIT}
        className="flex items-center gap-2 rounded border border-amber-400/30 bg-amber-400/[0.06] px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-amber-200 transition-colors hover:bg-amber-400/15 disabled:opacity-50"
      >
        {reconcile.isPending
          ? <Loader2 size={13} className="animate-spin" />
          : <ListChecks size={13} />}
        Reconcile deals
      </button>
      <button
        type="button"
        onClick={() => gapCensus.mutate()}
        disabled={sweep.isPending || reconcile.isPending || gapCensus.isPending}
        title="For every empty field on every deal, say whether a source states it — reads only, writes nothing"
        className="flex items-center gap-2 rounded border border-stone-700 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-stone-300 transition-colors hover:bg-stone-400/10 disabled:opacity-50"
      >
        {gapCensus.isPending
          ? <Loader2 size={13} className="animate-spin" />
          : <Search size={13} />}
        Why fields are empty
      </button>
    </div>
  )

  /** A found datapoint, added or dismissed — one row, one verb. */
  const decideFound = async (
    item: (typeof found)[number],
    verb: 'accept' | 'dismiss',
    edits?: { name: string; label: string; kind: string },
  ) => {
    setBusy(item.id)
    setError(null)
    setNotice(null)
    setRefused(null)
    try {
      const result = await dataReview.decide({
        itemId: item.id, verb, discovery: verb === 'accept' ? edits : undefined,
      })
      await invalidateAfterDecision(queryClient)
      setNotice(
        verb === 'dismiss'
          ? `“${item.label}” dismissed — not a datapoint.`
          : result.outcome === 'field_created'
            ? `“${item.label}” added as a new field.`
            : `The parser now knows “${item.label}” as a word for an existing field.`,
      )
    } catch (e) {
      // The server holds an Add to the evidence the row shows (409 with its
      // reason); a row decided elsewhere since is refused too, and the read
      // below then drops it — the reason moves to the foot of the list.
      setRefused({ id: item.id, message: refusalOf(e).message })
      void queryClient.invalidateQueries({ queryKey: dataReviewKeys.all })
    } finally {
      setBusy(null)
    }
  }

  const pick = (ids: string[], on: boolean) =>
    setPicked((current) => {
      const next = new Set(current)
      for (const id of ids) {
        if (on) next.add(id)
        else next.delete(id)
      }
      return next
    })

  /** Ask the server what a bulk dismissal would cover. Writes nothing. */
  const askDismiss = async (what: string, input: BulkDismissal['input'], note = '') => {
    setError(null)
    setNotice(null)
    setRefused(null)
    setBulk({ what, input, preview: null, note, applying: false, error: null })
    try {
      const preview = await dataReview.dismissDiscovery({ ...input, preview: true })
      // A preview that lands after its dialog was closed, or another asked for, is dropped.
      setBulk((current) => (current?.input === input ? { ...current, preview } : current))
    } catch (e) {
      setBulk((current) => (current?.input === input ? { ...current, error: refusalOf(e).message } : current))
    }
  }

  /** The second click: dismiss what the preview counted. */
  const applyDismiss = async () => {
    if (!bulk?.preview || bulk.applying) return
    const { preview, input, note } = bulk
    setBulk({ ...bulk, applying: true, error: null })
    try {
      // Exactly the rows the preview counted, so a label that arrived since is
      // not swept up with them — and the criterion it was asked with beside
      // them: the server narrows by both, so a row that stopped answering it
      // stays, and every row's audit event says what the set was named by. A
      // preview that did not list every row it counted is applied by the
      // criterion alone.
      const rows = preview.ids.length >= preview.count ? { ...input, ids: preview.ids } : input
      const result = await dataReview.dismissDiscovery({ ...rows, preview: false, note: note.trim() || undefined })
      setBulk(null)
      setPicked(new Set())
      setNotice(
        `Dismissed ${plural(result.count, 'found datapoint')} — each recorded against your name as not a datapoint.`,
      )
      await invalidateAfterDecision(queryClient)
    } catch (e) {
      setBulk((current) => (current ? { ...current, applying: false, error: refusalOf(e).message } : current))
      void queryClient.invalidateQueries({ queryKey: dataReviewKeys.all })
    }
  }

  const foundRow = (item: FoundItem) => (
    <DiscoveredRow
      key={item.id}
      row={item.detail.discovered}
      busy={busy === item.id}
      disabled={!mayAddFound}
      selected={picked.has(item.detail.discovered.id)}
      onSelect={mayAddFound ? (on) => pick([item.detail.discovered.id], on) : undefined}
      refusal={refused?.id === item.id ? refused.message : null}
      onAccept={(edits) => void decideFound(item, 'accept', edits)}
      onDismiss={() => void decideFound(item, 'dismiss')}
    />
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto scrollbar-thin">
      <div className="flex-shrink-0 rounded border border-amber-400/10 bg-black/20 px-3 py-2.5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ShieldQuestion size={13} className="text-amber-400" />
              <h2 className="text-[12px] font-medium uppercase tracking-wide text-stone-200">
                Data Intelligence Review
              </h2>
            </div>
            {totals && (
              <>
              <p className="mt-1 text-[10px] text-stone-500">
                {plural(totals.open, 'decision')} owed on {plural(totals.recordsOwed ?? totals.deals, 'record')}
                {totals.pipelineCritical > 0 && (
                  <span className="text-amber-300/80">
                    {' '}· {totals.pipelineCritical} on the pipeline grid
                  </span>
                )}
                {totals.humanDecided > 0 && (
                  <span className="text-amber-300/80">
                    {' '}· {totals.humanDecided} touch a value a person decided
                  </span>
                )}
              </p>
              {describeSettled(totals.settled).length > 0 && (
                <p data-testid="review-settled" className="mt-0.5 text-[10px] text-stone-600">
                  Also listed, not owed: {describeSettled(totals.settled).join(' · ')}
                </p>
              )}
              {discovery?.available && totals.discovery > 0 && (
                <p data-testid="review-found" className="mt-0.5 text-[10px] text-stone-600">
                  {plural(totals.discovery, 'found datapoint')}
                  {totals.discoveryAddable != null && totals.discoveryWatching != null
                    ? ` — ${totals.discoveryAddable} can be added now; ${totals.discoveryWatching} are stated on one deal or one file and cannot be added yet`
                    : ''}
                </p>
              )}
              </>
            )}
          </div>
          {deskActions}
        </div>

        {!mayDecide && (
          <p role="note" className="mt-2 text-[10px] leading-relaxed text-stone-400">
            {NEEDS_EDIT} Re-parsing and reconciling the desk are off for the same reason; “Why
            fields are empty” only reads, so it stays on.
          </p>
        )}
        {sweep.isError && (
          <p className="mt-2 text-[10px] text-red-300">The sweep failed. Nothing was written.</p>
        )}
        {sweep.isSuccess && (
          <p className="mt-2 text-[10px] text-stone-500">
            Examined {sweep.data.examined}; {sweep.data.recordsWithProposals} now carry
            proposals. Nothing has been written to a deal.
          </p>
        )}
        {gapCensus.isPending && censusProgress && (
          <p className="mt-2 text-[10px] text-stone-400">
            Reading the sources — {censusProgress.done} deal
            {censusProgress.done === 1 ? '' : 's'} censused
            {censusProgress.remaining > 0
              ? `, ${censusProgress.remaining} to go`
              : ''}{' '}
            (request {censusProgress.round}).
          </p>
        )}
        {gapCensus.isError && (
          <p className="mt-2 text-[10px] text-red-300">
            The census failed. It writes nothing, so nothing was changed.
          </p>
        )}
        {reconcile.isPending && progress && (
          <p className="mt-2 text-[10px] text-stone-400">
            Reconciling — {progress.done} deal{progress.done === 1 ? '' : 's'} done
            {progress.remaining > 0 ? `, ${progress.remaining} to go` : ''} (batch {progress.round})
          </p>
        )}
        {reconcile.isError && (
          <p className="mt-2 text-[10px] text-red-300">
            The reconciliation failed. Nothing was written to a deal that this run
            had not already finished; run it again to continue.
          </p>
        )}
        {report && (
          <p className="mt-2 flex items-center gap-1 text-[10px] text-stone-400">
            {report.applied
              ? <CheckCircle2 size={10} className="text-emerald-300/90" />
              : <ListChecks size={10} />}
            {report.applied ? 'Applied' : 'Preview'}: {report.deals.length} deal{report.deals.length === 1 ? '' : 's'},{' '}
            {report.totals.writes ?? 0} write{(report.totals.writes ?? 0) === 1 ? '' : 's'},{' '}
            {report.totals.needsPerson ?? 0} need a person
          </p>
        )}
        {latestSweep?.run && (
          <button
            type="button"
            onClick={() => {
              if (!latestSweep.run?.report) return
              setCensus(null)
              setReport(latestSweep.run.report)
            }}
            disabled={!latestSweep.run.report}
            title={
              latestSweep.run.status === 'failed'
                ? latestSweep.run.detail ?? 'The last automated sweep failed.'
                : latestSweep.run.status === 'complete'
                  ? 'View what the last automated sweep wrote'
                  : (latestSweep.run.status === 'partial'
                    ? 'The automated sweep stopped before finishing. View the partial report.'
                    : latestSweep.run.status === 'cancelled'
                      ? 'The automated sweep was cancelled. View any writes already committed.'
                      : 'The automated sweep is still running.') +
                    (latestSweep.run.detail ? ` ${latestSweep.run.detail}` : '')
            }
            className="mt-2 flex w-full items-center gap-1 text-left text-[10px] text-stone-500 hover:text-amber-300 disabled:cursor-default disabled:hover:text-stone-500"
          >
            {latestSweep.run.status === 'complete'
              ? <CheckCircle2 size={10} role="img" aria-label="complete sweep" className="text-emerald-300/60" />
              : latestSweep.run.status === 'running'
                ? <Loader2 size={10} role="img" aria-label="running sweep" className="animate-spin text-stone-400" />
                : latestSweep.run.status === 'cancelled'
                  ? <CircleSlash size={10} role="img" aria-label="cancelled sweep" className="text-stone-400" />
                  : <AlertTriangle size={10} role="img" aria-label={`${latestSweep.run.status} sweep`}
                    className={latestSweep.run.status === 'failed' ? 'text-red-300' : 'text-amber-300'} />}
            Last automated sweep ({new Date(latestSweep.run.createdAt).toLocaleString()}):{' '}
            {latestSweep.run.status === 'failed'
              ? 'failed'
              : latestSweep.run.status === 'running'
                ? 'running'
                : (latestSweep.run.status === 'complete' ? '' : `${latestSweep.run.status} — `) +
                `${latestSweep.run.writes} write${latestSweep.run.writes === 1 ? '' : 's'}, ` +
                `${latestSweep.run.needsPerson} need${latestSweep.run.needsPerson === 1 ? 's' : ''} a person` +
                (latestSweep.run.status === 'complete' && latestSweep.run.engineMisses != null
                  ? `, ${latestSweep.run.engineMisses} engine miss${latestSweep.run.engineMisses === 1 ? '' : 'es'}` +
                    (sweepUnsearched > 0
                      ? ` — ${sweepUnsearched} of ${sweepCensused || sweepUnsearched} deal${
                          (sweepCensused || sweepUnsearched) === 1 ? '' : 's'
                        } could not be searched (stored package unavailable), so this is not comparable with the last sweep`
                      : latestSweep.run.engineMissesBefore != null
                        ? ` (was ${latestSweep.run.engineMissesBefore})`
                        : '')
                  : '') +
                (sweepHeld > 0
                  ? ` · ${sweepHeld} deal${sweepHeld === 1 ? '' : 's'} held — stored package unavailable, re-upload before applying`
                  : '')}
          </button>
        )}
      </div>

      {/* ── The records ── */}
      <div className="flex-shrink-0 rounded border border-amber-400/10 bg-black/20">
        <div className="border-b border-amber-400/10 px-3 py-2">
          <span className="text-[11px] font-medium uppercase tracking-wide text-stone-300">
            Awaiting a decision
          </span>
        </div>
        {isLoading && <p className="px-3 py-4 text-[11px] text-stone-500">Loading…</p>}
        {isError && (
          <p role="alert" className="px-3 py-4 text-[11px] text-red-300">
            Could not load the review: {refusalOf(loadError).message}
          </p>
        )}
        {!isLoading && !isError && deals.length === 0 && (
          <p className="px-3 py-4 text-[11px] leading-relaxed text-stone-500">
            Nothing is waiting. Every record reads the same way the engine reads it now, or
            its questions have already been answered. Run the sweep to re-read every stored
            package with the current engine — it writes nothing, and every difference becomes
            a proposal you answer field by field. Reconcile deals runs every fill on every
            promoted deal — preview first, then apply.
          </p>
        )}
        {deals.length > 0 && (
          <table className="dense-table">
            <thead>
              <tr>
                <th>Record</th>
                <th>Status</th>
                <th className="text-right">Open</th>
                <th className="text-right">On the grid</th>
                <th className="text-right">Decided</th>
                <th>Where from</th>
                <th>First questions</th>
              </tr>
            </thead>
            <tbody>
              {deals.map((deal) => (
                <tr
                  key={deal.ingestionId}
                  className="cursor-pointer"
                  onClick={() => setPanelFor(deal.ingestionId)}
                >
                  <td className="max-w-[260px]">
                    <button
                      type="button"
                      className="block w-full truncate text-left text-[11px] text-stone-100 hover:text-amber-200"
                      title={deal.subject ?? deal.ingestionId}
                    >
                      {deal.property ?? deal.subject ?? deal.ingestionId}
                    </button>
                    <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-stone-500">
                      <span className="font-mono">{deal.ingestionId}</span>
                      {deal.loanId && <span>· deal <span className="font-mono">{deal.loanId}</span></span>}
                    </div>
                  </td>
                  <td>
                    <span className={`rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wide ${statusTone(deal.status)}`}>
                      {deal.status}
                    </span>
                  </td>
                  <td className="text-right font-mono tabular-nums text-stone-100">{deal.open}</td>
                  <td className="text-right font-mono tabular-nums">
                    {deal.pipelineCritical > 0 ? (
                      <span className="inline-flex items-center gap-1 text-amber-300">
                        <Flag size={9} /> {deal.pipelineCritical}
                      </span>
                    ) : <span className="text-stone-600">—</span>}
                  </td>
                  <td className="text-right font-mono tabular-nums">
                    {deal.humanDecided > 0 ? (
                      <span className="inline-flex items-center gap-1 text-amber-300/80">
                        <UserCheck size={9} /> {deal.humanDecided}
                      </span>
                    ) : <span className="text-stone-600">—</span>}
                  </td>
                  <td className="whitespace-nowrap text-[10px] text-stone-400">
                    {(['review', 'reparse', 'reconcile'] as const)
                      .filter((source) => deal.counts[source] > 0)
                      .map((source) => `${SOURCE_LABEL[source]} ${deal.counts[source]}`)
                      .join(' · ')}
                  </td>
                  <td className="max-w-[320px] truncate text-[10.5px] text-stone-400" title={firstQuestions(deal, 99)}>
                    {firstQuestions(deal)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {page?.hasMore && (
          <div
            data-testid="inbox-page"
            className="flex flex-wrap items-center justify-between gap-2 border-t border-amber-400/10 px-3 py-2 text-[10px] text-stone-400"
          >
            <span>
              Showing the {page.returned} worst of {page.total} records — the counts above are
              the whole desk’s.
            </span>
            <button
              type="button"
              onClick={() => setLimit(limit + INBOX_PAGE)}
              disabled={isFetching}
              className="rounded border border-stone-700 px-2 py-1 text-[10px] uppercase tracking-wide text-stone-300 transition-colors hover:bg-stone-400/10 disabled:opacity-50"
            >
              {isFetching ? 'Loading…' : `Show ${plural(Math.min(INBOX_PAGE, page.total - page.returned), 'more record')}`}
            </button>
          </div>
        )}
      </div>

      {/* ── Found new datapoints ── */}
      {discovery && (
        <div className="flex-shrink-0 rounded border border-amber-400/10 bg-black/20">
          <div className="flex items-center justify-between gap-2 border-b border-amber-400/10 px-3 py-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-stone-300">
              Found new datapoints
            </span>
            {discovery.available && discovery.total > 0 && (
              <span className="flex flex-wrap items-center gap-x-3 text-[10px]">
                <span className={BAND.auto.text} title={BAND.auto.note}>
                  {discovery.bands.auto} strong evidence
                </span>
                <span className={BAND.review.text} title={BAND.review.note}>
                  {discovery.bands.review} worth your read
                </span>
                <span className={BAND.weak.text} title={BAND.weak.note}>
                  {discovery.bands.weak} thin evidence
                </span>
              </span>
            )}
          </div>
          {!discovery.available ? (
            <p className="px-3 py-3 text-[11px] leading-relaxed text-stone-400">{discovery.reason}</p>
          ) : (
            <>
              {found.length > 0 && (
                <>
                  <p className="px-3 pt-2 text-[10px] leading-relaxed text-stone-500">
                    Labels the documents keep using that no field holds yet. Adding one changes what
                    every deal records, so each is a person’s decision — the engine only ever teaches
                    the parser a word for a field that exists. Every count opens to the deals, files
                    and readings behind it.
                  </p>
                  {discovery.truncated && (
                    <p className="px-3 pt-1 text-[10px] text-amber-300/80" data-testid="found-truncated">
                      Showing the {found.length} with the strongest evidence of {discovery.total}; the
                      rest follow as these are answered.
                    </p>
                  )}
                  {!mayAddFound && (
                    <p role="note" className="px-3 pt-1 text-[10px] leading-relaxed text-stone-400">
                      Adding or dismissing a found datapoint needs Intake and Pipeline edit access,
                      which this account does not both hold — these are here to read.
                    </p>
                  )}
                </>
              )}
              {(found.length > 0 || hiddenUnbacked > 0 || showUnbacked) && (
                <div className="space-y-2 px-3 pt-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="relative min-w-[200px] flex-1">
                      <Search
                        size={11}
                        className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-stone-600"
                      />
                      <input
                        type="search"
                        value={foundFilter}
                        onChange={(e) => setFoundFilter(e.target.value)}
                        placeholder="Filter by label or file…"
                        aria-label="Filter found datapoints"
                        className="w-full rounded border border-stone-800 bg-black/40 py-1 pl-6 pr-2 text-[10px] text-stone-200 outline-none placeholder:text-stone-600 focus:border-amber-400/40"
                      />
                    </div>
                    <button
                      type="button"
                      aria-pressed={byFile}
                      onClick={() => setByFile((on) => !on)}
                      title="List each found datapoint under the file it was read from"
                      className={`${TOGGLE} ${toggleTone(byFile)}`}
                    >
                      Group by file
                    </button>
                    {unbacked !== null && (unbacked > 0 || showUnbacked) && (
                      <button
                        type="button"
                        aria-pressed={showUnbacked}
                        onClick={() => setIncludeUnbacked(!showUnbacked)}
                        title="Found datapoints no live deal states any more — every reading came from a record that no longer exists. They are left off the list unless shown."
                        className={`${TOGGLE} ${toggleTone(showUnbacked)}`}
                      >
                        Show unbacked ({unbacked})
                      </button>
                    )}
                  </div>
                  {mayAddFound && (
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="flex items-center gap-1.5 text-[10px] text-stone-400">
                        <input
                          type="checkbox"
                          checked={allShownPicked}
                          disabled={shownIds.length === 0}
                          onChange={(e) => pick(shownIds, e.target.checked)}
                          className="h-3 w-3 accent-amber-400"
                        />
                        Select all shown
                      </label>
                      <button
                        type="button"
                        disabled={pickedIds.length === 0}
                        onClick={() => void askDismiss(
                          `The ${plural(pickedIds.length, 'found datapoint')} you selected.`,
                          { ids: pickedIds },
                        )}
                        className={BULK_BUTTON}
                      >
                        Dismiss selected ({pickedIds.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => void askDismiss(
                          'Every found datapoint whose label is a general-ledger account line — an account '
                            + 'code and name, such as “7010.000 - maint equipment”.',
                          { accountCodes: true },
                          'A general-ledger account line — a chart of accounts, not a deal datapoint.',
                        )}
                        title="Labels such as “7010.000 - maint equipment” are a chart of accounts, not deal datapoints. Shows what it would dismiss first."
                        className={BULK_BUTTON}
                      >
                        Dismiss account-code lines
                      </button>
                      {showUnbacked && unbacked !== null && unbacked > 0 && (
                        <button
                          type="button"
                          onClick={() => void askDismiss(
                            'Every found datapoint no live deal states any more — each of its readings came '
                              + 'from a record that no longer exists.',
                            { unbacked: true },
                            'No live deal states it — every reading came from a record that no longer exists.',
                          )}
                          className={BULK_BUTTON}
                        >
                          Dismiss unbacked ({unbacked})
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
              {found.length === 0 ? (
                <p className="px-3 py-3 text-[11px] text-stone-500">
                  {hiddenUnbacked > 0
                    ? `No label a live deal states is waiting. ${plural(hiddenUnbacked, 'found datapoint')} no live deal states any more ${hiddenUnbacked === 1 ? 'is' : 'are'} left off the list — show ${hiddenUnbacked === 1 ? 'it' : 'them'} to review or dismiss.`
                    : 'No labels are waiting. The documents state nothing this platform has no field for.'}
                </p>
              ) : shown.length === 0 ? (
                <p className="px-3 py-3 text-[11px] text-stone-500">Nothing matches “{foundFilter.trim()}”.</p>
              ) : byFile ? (
                <div className="space-y-2 p-3">
                  {groupFoundByFile(shown, (item) => item.detail.discovered).map((group) => {
                    const file = group.document
                    return (
                      <section
                        key={file ?? ''}
                        data-testid="found-file-group"
                        data-document={file ?? ''}
                        className="rounded border border-stone-800/70"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-800/70 px-3 py-1.5">
                          <div className="flex min-w-0 items-baseline gap-2">
                            <h3 className="truncate text-[12px] font-medium text-stone-200" title={file ?? undefined}>
                              {file ?? 'No file named'}
                            </h3>
                            <span className="shrink-0 text-[9px] text-stone-500">{plural(group.items.length, 'label')}</span>
                          </div>
                          {mayAddFound && file && (
                            <button
                              type="button"
                              // The rows this group lists, and the file: the server
                              // keeps those no other file states, so the button
                              // reaches nothing the person cannot see under it.
                              onClick={() => void askDismiss(
                                `Every found datapoint listed under ${file} that no other file states — `
                                  + 'a label another file also states is left for its own decision.',
                                { document: file, ids: group.items.map((item) => item.detail.discovered.id) },
                                `Dismissed with every label read from ${file}.`,
                              )}
                              className={BULK_BUTTON}
                            >
                              Dismiss all from this file
                            </button>
                          )}
                        </div>
                        <ul className="space-y-1.5 p-2">{group.items.map(foundRow)}</ul>
                      </section>
                    )
                  })}
                </div>
              ) : (
                <ul className="space-y-1.5 p-3">{shown.map(foundRow)}</ul>
              )}
            </>
          )}
        </div>
      )}

      {failure && (
        <p role="alert" className="flex-shrink-0 rounded border border-red-500/20 bg-red-500/10 px-3 py-2 text-[11px] text-red-300">
          {failure}
        </p>
      )}
      {notice && !failure && (
        <p role="status" className="flex-shrink-0 rounded border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-300">
          {notice}
        </p>
      )}

      {/* ── What the desk actions reported ── */}
      {census ? (
        <div ref={reportRef} className="flex-shrink-0 rounded border border-amber-400/10 bg-black/20 p-3">
          <GapCensusReport census={census} onOpenRecord={(id) => setPanelFor(id)} />
        </div>
      ) : report ? (
        <div ref={reportRef} className="flex-shrink-0 rounded border border-amber-400/10 bg-black/20 p-3">
          <ReconcileReport
            report={report}
            applying={reconcile.isPending}
            onApply={() => reconcile.mutate(true)}
            canApply={mayDecide}
            onOpenRecord={(id) => setPanelFor(id)}
            pendingIds={new Set(deals.map((d) => d.ingestionId))}
          />
        </div>
      ) : null}

      {bulk && (
        <DismissConfirmation
          bulk={bulk}
          offList={previewOffList}
          onNote={(note) => setBulk((current) => (current ? { ...current, note } : current))}
          onApply={() => void applyDismiss()}
          onClose={() => {
            if (!bulk.applying) setBulk(null)
          }}
        />
      )}

      {panelFor && <DataReviewPanel ingestionId={panelFor} onClose={() => setPanelFor(null)} />}
    </div>
  )
}

/**
 * The confirmation a bulk dismissal waits on: the server's count of what it
 * would dismiss, the first labels by name, and the note each dismissal will
 * carry. The apply button is the count the preview showed; a preview that
 * found nothing offers nothing to apply.
 */
function DismissConfirmation({
  bulk,
  offList,
  onNote,
  onApply,
  onClose,
}: {
  bulk: BulkDismissal
  /** What the count includes that the list above does not show, when anything. */
  offList: string | null
  onNote: (note: string) => void
  onApply: () => void
  onClose: () => void
}) {
  const preview = bulk.preview
  const count = preview?.count ?? 0
  const listed = preview?.labels.slice(0, LISTED_LABELS) ?? []
  const rest = count - listed.length
  return (
    <DialogShell
      label="Dismiss found datapoints"
      onClose={onClose}
      width="md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={bulk.applying}
            className="rounded px-2 py-1 text-[10.5px] text-stone-400 hover:text-stone-200 disabled:opacity-40"
          >
            {count > 0 ? 'Cancel' : 'Close'}
          </button>
          {count > 0 && (
            <button
              type="button"
              onClick={onApply}
              disabled={bulk.applying}
              className="flex items-center gap-1.5 rounded border border-amber-400/40 bg-amber-400/10 px-2.5 py-1 text-[10px] uppercase tracking-wide text-amber-200 transition-colors hover:bg-amber-400/20 disabled:opacity-50"
            >
              {bulk.applying ? <Loader2 size={11} className="animate-spin" /> : null}
              Dismiss {count}
            </button>
          )}
        </>
      }
    >
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-3">
        <p className="text-[11px] leading-relaxed text-stone-300">{bulk.what}</p>
        {!preview && !bulk.error && (
          <p className="flex items-center gap-1.5 text-[10px] text-stone-500">
            <Loader2 size={11} className="animate-spin" />
            Counting what this would dismiss — nothing is written yet.
          </p>
        )}
        {preview && count === 0 && (
          <p className="text-[10.5px] text-stone-400">Nothing matches — nothing would be dismissed.</p>
        )}
        {preview && count > 0 && (
          <>
            <p className="text-[10.5px] leading-relaxed text-stone-300">
              {plural(count, 'found datapoint')} would be dismissed as not a datapoint — each recorded
              against your name exactly as if dismissed by hand. None is added, and nothing is written
              until you confirm.
            </p>
            {offList && <p className="text-[10.5px] leading-relaxed text-amber-300/80">{offList}</p>}
            {listed.length > 0 && (
              <ul
                aria-label="Labels it would dismiss"
                className="space-y-0.5 rounded border border-stone-800 bg-black/25 px-2 py-1.5"
              >
                {listed.map((label, i) => (
                  <li key={i} className="truncate text-[10px] text-stone-300">“{label}”</li>
                ))}
              </ul>
            )}
            {listed.length > 0 && rest > 0 && <p className="text-[10px] text-stone-500">+ {rest} more</p>}
            <label className="block">
              <span className="text-[8px] uppercase tracking-wide text-stone-600">
                Note — recorded with each dismissal
              </span>
              <input
                value={bulk.note}
                onChange={(e) => onNote(e.target.value)}
                disabled={bulk.applying}
                className="mt-0.5 w-full rounded border border-stone-800 bg-black/40 px-1.5 py-1 text-[10px] text-stone-200 outline-none focus:border-amber-400/40"
              />
            </label>
          </>
        )}
        {bulk.error && <p className="text-[10.5px] leading-relaxed text-red-300">{bulk.error}</p>}
      </div>
    </DialogShell>
  )
}
